import React, { useState, useRef, ReactElement, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Tooltip,
  Box,
  Collapse,
  Checkbox
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RenderTooltipContent from "./RenderTooltipContent";
import "./CustomTablewithTotalInfo.css";
import { truncateText } from "../../features/stocks/RealTimeStock/RealTimeStockUtils";
import { truncateToTwoDecimals } from "./DmsUtils";
import { Loader } from "../loader/Loader";

interface Column {
  currencySymbol?: boolean;
  headerName: string;
  mappedValue: string;
  textAlign?: string;
  width?: number;
  renderCell?: (value: any, row: any) => React.ReactNode;
  renderHeader?: (value: any) => React.ReactNode;
  truncateTextAfterChar?: number;
  canOpen?: boolean;
}

interface CustomTableProps {
  columns: Column[];
  data: any[];
  totals: any[];
  children?: ReactElement;
  headerBackgroundColor?: string;
  isEditing?: boolean;
  isArs?: boolean;
  setScrollTop?: React.Dispatch<React.SetStateAction<number>>;
  isScrolling?: boolean
  allowTableScroll?: boolean;
  hasRealChanges?:boolean;
  editLoading?:boolean;
  expandableRowColumns?: any[];
  isCheckboxSelection?: boolean;
  checkedIdArray?: any[];
  setCheckedIdArray?: (ids: any[]) => void;
  rowIdsWithCheckboxHidden?: any[];
  selectableRowIds?: any[];
  summaryFooterData?:any;
  handleClosePopover?: () => void;
  customMessageForNoData?: string;
  selectedInvoiceSection?:string;
  selectedInvoiceTrigger?:number;
}

const CustomTablewithTotalInfoV2: React.FC<CustomTableProps> = ({
  columns,
  data,
  totals,
  children,
  headerBackgroundColor,
  isEditing,
  isArs,
  setScrollTop = () => {},
  isScrolling,
  allowTableScroll = true,
  hasRealChanges,
  editLoading,
  expandableRowColumns,
  isCheckboxSelection = false,
  checkedIdArray = [],
  setCheckedIdArray,
  rowIdsWithCheckboxHidden = [],
  selectableRowIds,
  summaryFooterData,
  handleClosePopover,
  customMessageForNoData,
  selectedInvoiceSection,
  selectedInvoiceTrigger
}) => { const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectionChanged,setSelectionChanged]=useState<boolean>(false);
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const tableHeaderRef = useRef<HTMLTableSectionElement | null>(null);

  const currencySymbol = ConfigDictionary.currency;
  const designationBasedUomSecondary = ConfigDictionary.designationBasedUomSecondary;
   const sectionRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({
    all_invoiced: null,
    invoiced_not_ordered: null,
    ordered_not_invoiced: null
  });

  const isScrollingToSection = useRef(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const paginatedData = data?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
   
  useEffect(() => {
  if (!selectedInvoiceSection) return;

  const sectionKey = selectedInvoiceSection;

  if (sectionKey === "all_invoiced") {
    setPage(0);
    setSelectionChanged((prev)=>!prev)
    return;
  }

  if(groupedData?.[sectionKey]?.length>0){
  const pageIndex = sectionKey === "invoiced_not_ordered"
    ? Math.floor((groupedData?.["all_invoiced"]?.length ?? 0) / rowsPerPage)
    : Math.floor(
        ((groupedData?.["all_invoiced"]?.length ?? 0) +
         (groupedData?.["invoiced_not_ordered"]?.length ?? 0)
        ) / rowsPerPage
      );
  setPage(pageIndex);
  setSelectionChanged((prev)=>!prev)
  }

}, [selectedInvoiceSection,selectedInvoiceTrigger]);

useEffect(() => {
 
  if (!tableref.current||!selectedInvoiceSection) return;
  //  const scrollTopValue = tableref.current.scrollTop;
   isScrollingToSection.current = true;
    if (selectedInvoiceSection === 'all_invoiced') {
    tableref.current.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setTimeout(() => {
      isScrollingToSection.current = false;
    }, 600);
    return;
  }
   
  const container = tableref.current;
  const sectionElement = sectionRefs.current[selectedInvoiceSection];
  if (!sectionElement) return;
  const tableHeaderHeight = tableHeaderRef.current?.offsetHeight || 57;
  const sectionOffsetTop = sectionElement.offsetTop;
  const targetScrollTop = sectionOffsetTop - tableHeaderHeight - 10;

  tableref.current.scrollTo({
    top: Math.max(0, targetScrollTop),
    behavior: 'smooth'
  });

  setTimeout(() => {
    isScrollingToSection.current = false;
  }, 600);
}, [selectionChanged]); 


  const SECTION_ORDER = [
  'all_invoiced',
  'invoiced_not_ordered',
  'ordered_not_invoiced'
]

  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];
  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);
  function isNumeric(value: unknown): value is number | string {
    return (
      (typeof value === "string" || typeof value === "number") &&
      !isNaN(Number(value))
    );
  }

  if(designationEnabled && designation && designationKeys && matchedRole){
    const isCaseAllowed = matchedRole?.uomAllowed?.case?.trim();

    const elementsToRemove = isCaseAllowed
    ? ["pieceQuantity", "otherUnitQuantity"]
    : ["caseQuantity", "otherUnitQuantity"];

    columns = columns.filter(element => !elementsToRemove.includes(element.mappedValue));

    // const elementToKeep = matchedRole?.uomAllowed?.case?.trim() ? "Cs" : "Pc";
    
    // const column = columns.find(col => col.mappedValue === 'price');

    // if (column) {
    //   column.headerName = `Price (Each ${elementToKeep})`; 
    // }
  }
  
  const renderSkusCell = (skus: string[] = []) => {
    if (skus.length === 0) {
      return "-";
    }

    return (
      <Tooltip
        title={<RenderTooltipContent items={skus} title="SKU Code & Name" />}
        placement="right"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#FFFFFF !important",
              color: "#000000 !important",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
            },
          },
          arrow: {
            sx: {
              color: "#FFFFFF !important",
            },
          },
        }}
      >
        <div style={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {skus[0]?.slice(0, 25)}…
          {skus.length > 1 && (
            <span style={{ color: "#2568E5", textDecoration: "underline" }}>
              +{skus.length - 1}
            </span>
          )}
        </div>
      </Tooltip>
    );
  };
 
const tableref = useRef<HTMLDivElement>(null);
useEffect(() => {
  const handleScrollEnd = () => {
    if (setScrollTop && tableref.current) {
      const scrollTopValue = tableref.current.scrollTop;
      setScrollTop(scrollTopValue);
    }
  }
  
  const el = tableref.current;
  if (el) {
    el.addEventListener("scrollend", handleScrollEnd)
  }

  return () => {
    if (el) {
      el.removeEventListener("scrollend", handleScrollEnd);
    }
  };
}, [setScrollTop, isScrolling]);

  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const toggleRow = (rowIndex: number) => {
    handleClosePopover && handleClosePopover();
    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  // Checkbox selection handlers
  const handleSelectRow = (rowId: any) => {
    if (!setCheckedIdArray) return;
    
    const newCheckedIds = [...checkedIdArray];
    const index = newCheckedIds.indexOf(rowId);
    
    if (index > -1) {
      newCheckedIds.splice(index, 1);
    } else {
      newCheckedIds.push(rowId);
    }
    
    setCheckedIdArray(newCheckedIds);
  };
  
  const handleSelectAll = () => {
    if (!setCheckedIdArray) return;
    
    const allRowIds = data.map(row => row.id).filter((id): id is any => id !== undefined);
    const filteredIds = allRowIds.filter(id => !rowIdsWithCheckboxHidden.includes(id));
    
    let selectableIds = filteredIds;
    if (selectableRowIds) {
      selectableIds = filteredIds.filter(id => selectableRowIds.includes(id));
    }
    
    // If all selectable rows are already selected, deselect all
    if (isAllSelected()) {
      setCheckedIdArray([]);
    } else {
      // Otherwise, select all selectable rows
      setCheckedIdArray(selectableIds);
    }
  };

  const isRowSelectable = (rowId: any) => {
    if (rowIdsWithCheckboxHidden.includes(rowId)) {
      return false;
    }
    if (selectableRowIds) {
      return selectableRowIds.includes(rowId);
    }
    return true;
  };

  const groupedData = selectedInvoiceSection
  ? SECTION_ORDER.reduce((acc:any, sectionKey:string) => {
      acc[sectionKey] = data.filter(
        row => (row.section || "all_invoiced") === sectionKey
      );
      return acc;
    }, {})
  : null;

    
const getSectionPaginatedData = (groupedData:any, page:number, rowsPerPage:number) => {
  if(!selectedInvoiceSection) return;
  const sectionWisepaginated: Record<string, any> = {};
  let allData: any[] = [];
  let total = 0;

  let remaining = rowsPerPage; 

  SECTION_ORDER.forEach(sectionKey => {
    if (remaining <= 0) {
      sectionWisepaginated[sectionKey] = [];
      return;
    }
    const rows = groupedData[sectionKey] || [];
    const start =Math.max((page*rowsPerPage)-total,0);
    const end = start + remaining;
    const pageRows = rows.slice(start, end);
     total += rows.length; 
    sectionWisepaginated[sectionKey] = pageRows;

    allData = [...allData, ...pageRows];
    remaining -= pageRows.length;
  });

  return  sectionWisepaginated ;
};

  const sectionLabels: { [key: string]: string } = {
    invoiced_not_ordered: 'Invoiced, But Not Ordered',
    ordered_not_invoiced: 'Ordered, But Not Invoiced'
  };

const sectionWisepaginated=getSectionPaginatedData(groupedData,page,rowsPerPage)

  const sectionColors: { [key: string]: string } = {
     invoiced_not_ordered: '#00c6b2',
    ordered_not_invoiced: '#dd2d2d'
  };

  const isAllSelected = () => {
    const selectableRows = data.filter(row => isRowSelectable(row.id));
    return selectableRows.length > 0 && selectableRows.every(row => checkedIdArray.includes(row.id));
  };

  const isIndeterminate = () => {
    const selectableRows = data.filter(row => isRowSelectable(row.id));
    const selectedSelectableRows = selectableRows.filter(row => checkedIdArray.includes(row.id));
    return selectedSelectableRows.length > 0 && selectedSelectableRows.length < selectableRows.length;
  };
  return (
    <>
    <div className="custom_table_wrapper ignore-scroll-detection">
      <TableContainer
        ref={tableref}
        sx={{
          overflowY:  allowTableScroll ? "scroll" : "hidden",
          height:"100%",
          maxHeight: "100%",
          scrollbarWidth: ConfigDictionary?.showTableScrollbar ? "thin" : "none",
          ...(!(ConfigDictionary?.showTableScrollbar) && {
            "&::-webkit-scrollbar": {
              display: "none",
            },
          })
        }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: columns.reduce(
              (sum: any, column: any) => sum + (column.width || 0),
              0
            ),
            position: "relative",
            borderCollapse: "collapse",
            height: "100%",
          }}
        >
          <TableHead ref={tableHeaderRef} style={{ position: "sticky", top: 0, zIndex: 3 }}>
            <TableRow
            
              style={{
                position: "sticky",
                top: 0,
                border: `1px solid ${headerBackgroundColor}`,
                backgroundColor: headerBackgroundColor || "#ffffff",
                zIndex: 2,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              {/* Checkbox column header */}
              {isCheckboxSelection && (
                <TableCell
                  className="custom_table_header"
                  sx={{
                    borderBottom: "0px !important",
                    textAlign: "center",
                    width: "50px",
                    minWidth: "50px",
                    maxWidth: "50px",
                    backgroundColor: headerBackgroundColor || "#ffffff",
                    position: "relative",
                  }}
                >
                  <Checkbox
                    checked={isAllSelected()}
                    indeterminate={isIndeterminate()}
                    onChange={handleSelectAll}
                    sx={{ padding: "4px" }}
                  />
                </TableCell>
              )}
              {columns.map((col: any, index: number) => (
                <TableCell
                  key={col.headerName + index}
                  className="custom_table_header"
                  sx={{
                    borderBottom: "0px !important",
                    textAlign: `${col?.textAlign || "left"}`,
                  }}
                  style={{
                    width: col.width,
                    minWidth: `${col.width}px`,
                    maxWidth: `${col.width}px`,
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                    alignContent: "center",
                    backgroundColor: headerBackgroundColor || "#ffffff",
                    position: "relative"
                  }}
                >
                  {col.renderHeader
                    ? col.renderHeader(col.headerName)
                    : col.headerName}
                  {col.addAsterisk && hasRealChanges && <span style={{position: "absolute", top: 0, right: 0, color: "#B54747"}}>*</span>}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              scrollbarWidth: (ConfigDictionary?.showTableScrollbar ?? false) ? "thin" : "none",
              overflowX: (ConfigDictionary?.showTableScrollbar ?? false) ? "hidden" : "auto",
              overflowY:  allowTableScroll ? "scroll" : "hidden",
              display: "flex",
              flexDirection: "column",
              height:"100%",
            }}
          >
            {editLoading ?
              <Loader />
            :
            data.length === 0 ? ( 
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{
                    textAlign: "center",
                    borderBottom: "none",
                    height: "200px",
                    display: 'flex',
                    justifyContent:"center"
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ fontSize: "14px", color: "#7C7C7C", width: "100%" }}
                  >
                    {
                      customMessageForNoData ? customMessageForNoData :  "No data found."
                      }
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
             selectedInvoiceSection && sectionWisepaginated ? (
              <>
                {
                SECTION_ORDER.map((sectionKey:string) => {
                  const sectionData = sectionWisepaginated[sectionKey]
                  if (!sectionData?.length) return null;
             return(    <React.Fragment key={sectionKey}>
                    {sectionKey !== 'all_invoiced' && sectionLabels[sectionKey] && sectionData?.length>0 &&(
                     <TableRow
  ref={(el) => {
    if (el) sectionRefs.current[sectionKey] = el;
  }}
  style={{
    backgroundColor: '#f9f9f9',
    display: "flex",
    alignItems: "center",
    borderTop: '0.5px solid #e0e0e0',
  }}
>
  <TableCell
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontWeight: 400,
      fontSize: '14px',
      color: '#333',
      padding: '4px 8px',
      width: '100%',
      borderBottom: "none",
    }}
  >
    <Box
      sx={{
        width: "3px",
        height: "16px",
        borderRadius: "4px",
        backgroundColor: sectionColors[sectionKey] || "#27A599", 
      }}
    />
    {sectionLabels[sectionKey]}
  </TableCell>
    </TableRow>

        )}
                {sectionData?.length>0&&sectionData.map((row: any, rowIndex: number) => {     
              if (row?.showMultipleRows) {
                return (
                  <React.Fragment key={`parent-${rowIndex+sectionKey}`}>
                    
                  <TableRow
                    key={rowIndex+sectionKey}
                    style={{
                      borderBottom: expandedRows[rowIndex]
                        ? "none"
                        : rowIndex === paginatedData.length - 1
                        ? "none"
                        : "1px solid #f0f0f0",
                      display: "flex",
                      justifyContent: "space-around"
                    }}
                  >
                    {/* Checkbox column cell */}
                    {isCheckboxSelection && (
                      <TableCell
                      
                        sx={{
                          alignContent: "center",
                          width: "50px",
                          minWidth: "50px",
                          maxWidth: "50px",
                          textAlign: "center",
                          border: "0px !important",
                          color: "#7C7C7C",
                          fontSize: "12px",
                        }}
                        className="custom_table_cell"
                      >
                        <Checkbox
                          checked={checkedIdArray.includes(row.id)}
                          disabled={!isRowSelectable(row.id)}
                          onChange={() => handleSelectRow(row.id)}
                          sx={{ padding: "4px" }}
                        />
                      </TableCell>
                    )}
                    {columns?.map((col, index) => {
                      let truncatedText = col?.renderCell
                        ? ""
                        : truncateText(
                            row[col.mappedValue]?.toString(),
                            col?.truncateTextAfterChar ?? +Infinity
                          );

                      let inputText = truncatedText;
                      let formattedValue;
                      if (col?.currencySymbol) {
                        truncatedText = `${currencySymbol}\u200A${truncatedText ?? ""}`;
                        if (isNumeric(inputText)) {
                          formattedValue = truncateToTwoDecimals(Number(inputText));
                        } else {
                          formattedValue = inputText;
                        }
                      }
                      truncatedText = `${currencySymbol}\u200A${formattedValue ?? ""}`;

                      const isEllipsisColumn =
                        col.mappedValue === "skuName" ||
                        col.mappedValue === "outletName" ||
                        col.mappedValue === "outletCode" ||
                        col.mappedValue === "remark";

                      if (col.mappedValue === "skus") {
                        return (
                          <TableCell
                            sx={{
                              alignContent: "center",
                              width: `${col.width}px`,
                              minWidth: `${col.width}px`,
                              maxWidth: `${col.width}px`,
                              textAlign: `${col?.textAlign || "left"}`,
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              border: "0px !important",
                              color: "#7C7C7C",
                              fontSize: "12px",
                            }}
                            key={index}
                            className="custom_table_cell"
                          >
                            {renderSkusCell(row.skus)}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          sx={{
                            alignContent: "center",
                            width: `${col.width}px`,
                            minWidth: `${col.width}px`,
                            maxWidth: `${col.width}px`,
                            textAlign: `${col?.textAlign || "left"}`,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: isEllipsisColumn ? "nowrap" : "normal",
                            overflow: isEllipsisColumn ? "hidden" : "visible",
                            textOverflow: isEllipsisColumn ? "ellipsis" : "clip",
                            border: "0px !important",
                            color: "#7C7C7C",
                            fontSize: "12px",
                          }}
                          key={index}
                          className="custom_table_cell"
                          onClick={col?.canOpen ? () => toggleRow(rowIndex) : undefined}
                        >
                          {col.renderCell ? (
                            col.renderCell(row[col.mappedValue], row)
                          ) : isEllipsisColumn ? (
                            <Tooltip title={row[col.mappedValue]?.toString()} arrow>
                              <span
                                style={{
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {truncatedText}
                              </span>
                            </Tooltip>
                          ) : (
                            truncatedText
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* Child rows under this parent */}
                  {expandedRows[rowIndex] && (
                    <TableRow
                      sx={{
                        "& .MuiTableRow-root": {
                          border: "none",
                          width: "100%",
                        },
                        "& .MuiTableCell-head": {
                          border: "none",
                        },
                        borderBottom:"1px solid #f0f0f0",
                      }}
                    >
                      <TableCell style={{ padding: "8px", border: "none !important", width: '100%', display: "flex" }}>
                        <Collapse in={expandedRows[rowIndex]} timeout="auto" unmountOnExit 
                          sx={{
                            width : "100%"
                          }}
                        >
                          <Box
                            margin={0}
                            sx={{
                              width: "100%",
                              paddingLeft: "12px",
                              borderLeft: "3px solid rgb(191, 191, 191)",
                            }}
                          >
                            <div
                              className="batch-stock-table-wrapper"
                              style={{
                                borderRadius: "4px",
                                overflow: "hidden",
                                border: "1px solid #eaeaea",
                              }}
                            >
                              <Table sx={{ width: "100%",
                                  minWidth: expandableRowColumns?.reduce(
                                  (sum: any, column: any) => sum + (column.width || 0),
                                  0,
                                ),zIndex: 1, }}>
                                {/* Child Table Head */}
                                <TableHead>
                              <TableRow
                                style={{
                                  backgroundColor: "#fafafa",
                                  zIndex: 2,
                                  display: "flex",
                                  justifyContent: "space-around",
                                }}
                              >
                                {expandableRowColumns?.map((col: any, index: number) => (
                                  <TableCell
                                    key={col.headerName + index}
                                    className="custom_table_header"
                                    sx={{
                                      borderBottom: "0px !important",
                                      textAlign: `${col?.textAlign || "left"}`,
                                      color: "#6f6f6f !important"
                                    }}
                                    style={{
                                      width: col.width,
                                      minWidth: `${col.width}px`,
                                      maxWidth: `${col.width}px`,
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                      whiteSpace: "normal",
                                      alignContent: "center",
                                      backgroundColor: "#fafafa",
                                      position: "relative",
                                    }}
                                  >
                                    {col.renderHeader
                                      ? col.renderHeader(col.headerName)
                                      : col.headerName}
                                    {col.addAsterisk && hasRealChanges && <span style={{position: "absolute", top: 0, right: 0, color: "#B54747"}}>*</span>}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>

                                {/* Child Table Body */}
                                <TableBody style={{border: "none !important"}}>
                                  {(row.mergedRows || []).map((child: any, childIndex: number) => (
                                    <TableRow
                                      key={`child-${rowIndex}-${childIndex}`}
                                      style={{
                                        borderBottom: "1px solid #f0f0f0",
                                        display: "flex",
                                        justifyContent: "space-around",
                                        background: "#fff",
                                        
                                      }}
                                    >
                                      {expandableRowColumns?.map((col, index) => {
                                        let truncatedText = col?.renderCell
                                          ? ""
                                          : truncateText(
                                              child[col.mappedValue]?.toString(),
                                              col?.truncateTextAfterChar ?? +Infinity
                                            );

                                        let inputText = truncatedText;
                                        let formattedValue;
                                        if (col?.currencySymbol) {
                                          truncatedText = `${currencySymbol}\u200A${truncatedText ?? ""}`;
                                          if (isNumeric(inputText)) {
                                            formattedValue = truncateToTwoDecimals(Number(inputText));
                                          } else {
                                            formattedValue = inputText;
                                          }
                                        }
                                        truncatedText = `${currencySymbol}\u200A${formattedValue ?? ""}`;

                                        const isEllipsisColumn =
                                          col.mappedValue === "skuName" ||
                                          col.mappedValue === "outletName" ||
                                          col.mappedValue === "outletCode" ||
                                          col.mappedValue === "remark";


                                        return (
                                          <TableCell
                                            key={index}
                                            sx={{
                                              width: `${col.width}px`,
                                              minWidth: `${col.width}px`,
                                              maxWidth: `${col.width}px`,
                                              fontSize: "12px",
                                              color: "#555",
                                              border: "0px !important",
                                              alignSelf: "center"
                                            }}
                                            className="custom_table_cell"
                                          >
                                            {col.renderCell ? (
                                              col.renderCell(child[col.mappedValue], child)
                                            ) : isEllipsisColumn ? (
                                                <Tooltip title={child[col.mappedValue]?.toString()} arrow>
                                                  <span
                                                    style={{
                                                      display: "inline-block",
                                                      maxWidth: "100%",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {truncatedText}
                                                  </span>
                                                </Tooltip>
                                            ) : (
                                              truncatedText
                                            )}
                                          </TableCell>
                                        );
                                      })}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}

                </React.Fragment>

                );
              }
               
              return (
              <TableRow
            
                key={rowIndex}
                style={{
                  borderBottom: rowIndex === paginatedData.length - 1 ? "none" : "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-around",
                  // background: row?.source?.toLowerCase() === "ars" ? "rgb(238, 238, 238)" : "transparent",
                }}
              >
                {/* Checkbox column cell */}
                {isCheckboxSelection && (
                  <TableCell
                    sx={{
                      alignContent: "center",
                      width: "50px",
                      minWidth: "50px",
                      maxWidth: "50px",
                      textAlign: "center",
                      border: "0px !important",
                      color: "#7C7C7C",
                      fontSize: "12px",
                    }}
                    className="custom_table_cell"
                  >
                    <Checkbox
                      checked={checkedIdArray.includes(row.id)}
                      disabled={!isRowSelectable(row.id)}
                      onChange={() => handleSelectRow(row.id)}
                      sx={{ padding: "4px" }}
                    />
                  </TableCell>
                )}
                {columns?.map((col, index) => {
                  let truncatedText = col?.renderCell
                    ? ""
                    : truncateText(
                        row[col.mappedValue]?.toString(),
                        col?.truncateTextAfterChar ?? +Infinity
                      );

                  let inputText = truncatedText; // your original string input
                  let formattedValue;
                  if (col?.currencySymbol) {
                    truncatedText = `${currencySymbol}\u200A${truncatedText ?? ""}`;
                    if (isNumeric(inputText)) {
                        formattedValue = truncateToTwoDecimals(Number(inputText));
                    } else {
                        formattedValue = inputText;
                    }
                  }
                  truncatedText = `${currencySymbol}\u200A${formattedValue ?? ""}`;

                  const isEllipsisColumn = col.mappedValue === "skuName" || col.mappedValue === "outletName" || col.mappedValue === "outletCode" || col.mappedValue === "remark";
                  if (col.mappedValue === "skus") {
                    return (
                      <TableCell
                        sx={{
                          alignContent: "center",
                          width: `${col.width}px`,
                          minWidth: `${col.width}px`,
                          maxWidth: `${col.width}px`,
                          textAlign: `${col?.textAlign || "left"}`,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          border: "0px !important",
                          color: "#7C7C7C",
                          fontSize: "12px",
                        }}
                        key={index}
                        className="custom_table_cell"
                      >
                        {renderSkusCell(row.skus)}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      sx={{
                        alignContent: "center",
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        maxWidth: `${col.width}px`,
                        textAlign: `${col?.textAlign || "left"}`,
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: isEllipsisColumn ? "nowrap" : "normal", 
                        overflow: isEllipsisColumn ? "hidden" : "visible", 
                        textOverflow: isEllipsisColumn ? "ellipsis" : "clip", 
                        border: "0px !important",
                        color: "#7C7C7C",
                        fontSize: "12px",
                      }}
                      key={index}
                      className="custom_table_cell"
                    >
                      {col.renderCell ? (
                        col.renderCell(row[col.mappedValue], row)
                      ) : isEllipsisColumn ? (
                          <Tooltip title={row[col.mappedValue]?.toString()} arrow>
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {truncatedText}
                            </span>
                          </Tooltip>
                      ) : (
                        truncatedText
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
       
            );
            
          }
                    )
                  }
          </React.Fragment>
                )    
              } )}
                </>
             )
         
      :(
        <>
        {
         paginatedData.map((row, rowIndex) => {
              if (row?.showMultipleRows) {
                return (
                  <React.Fragment key={`parent-${rowIndex}`}>
                  {/* Parent row (clickable to expand/collapse) */}
                  <TableRow
                 
                    key={rowIndex}
                    style={{
                      borderBottom: expandedRows[rowIndex]
                        ? "none"
                        : rowIndex === paginatedData.length - 1
                        ? "none"
                        : "1px solid #f0f0f0",
                      display: "flex",
                      justifyContent: "space-between",

                    }}
                  >
                    {/* Checkbox column cell */}
                    {isCheckboxSelection && (
                      <TableCell
                        sx={{
                          alignContent: "center",
                          width: "50px",
                          minWidth: "50px",
                          maxWidth: "50px",
                          textAlign: "center",
                          border: "0px !important",
                          color: "#7C7C7C",
                          fontSize: "12px",
                        }}
                        className="custom_table_cell"
                      >
                        <Checkbox
                          checked={checkedIdArray.includes(row.id)}
                          disabled={!isRowSelectable(row.id)}
                          onChange={() => handleSelectRow(row.id)}
                          sx={{ padding: "4px" }}
                        />
                      </TableCell>
                    )}
                    {columns?.map((col, index) => {
                      let truncatedText = col?.renderCell
                        ? ""
                        : truncateText(
                            row[col.mappedValue]?.toString(),
                            col?.truncateTextAfterChar ?? +Infinity
                          );

                      let inputText = truncatedText;
                      let formattedValue;
                      if (col?.currencySymbol) {
                        truncatedText = `${currencySymbol}\u200A${truncatedText ?? ""}`;
                        if (isNumeric(inputText)) {
                          formattedValue = truncateToTwoDecimals(Number(inputText));
                        } else {
                          formattedValue = inputText;
                        }
                      }
                      truncatedText = `${currencySymbol}\u200A${formattedValue ?? ""}`;

                      const isEllipsisColumn =
                        col.mappedValue === "skuName" ||
                        col.mappedValue === "outletName" ||
                        col.mappedValue === "outletCode" ||
                        col.mappedValue === "remark";

                      if (col.mappedValue === "skus") {
                        return (
                          <TableCell
                            sx={{
                              alignContent: "center",
                              width: `${col.width}px`,
                              minWidth: `${col.width}px`,
                              maxWidth: `${col.width}px`,
                              textAlign: `${col?.textAlign || "left"}`,
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              border: "0px !important",
                              color: "#7C7C7C",
                              fontSize: "12px",
                            }}
                            key={index}
                            className="custom_table_cell"
                          >
                            {renderSkusCell(row.skus)}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          sx={{
                            alignContent: "center",
                            width: `${col.width}px`,
                            minWidth: `${col.width}px`,
                            maxWidth: `${col.width}px`,
                            textAlign: `${col?.textAlign || "left"}`,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: isEllipsisColumn ? "nowrap" : "normal",
                            overflow: isEllipsisColumn ? "hidden" : "visible",
                            textOverflow: isEllipsisColumn ? "ellipsis" : "clip",
                            border: "0px !important",
                            color: "#7C7C7C",
                            fontSize: "12px",
                          }}
                          key={index}
                          className="custom_table_cell"
                          onClick={col?.canOpen ? () => toggleRow(rowIndex) : undefined}
                        >
                          {col.renderCell ? (
                            col.renderCell(row[col.mappedValue], row)
                          ) : isEllipsisColumn ? (
                            <Tooltip title={row[col.mappedValue]?.toString()} arrow>
                              <span
                                style={{
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {truncatedText}
                              </span>
                            </Tooltip>
                          ) : (
                            truncatedText
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* Child rows under this parent */}
                  {expandedRows[rowIndex] && (
                    <TableRow
                      sx={{
                        "& .MuiTableRow-root": {
                          border: "none",
                          width: "100%",
                        },
                        "& .MuiTableCell-head": {
                          border: "none",
                        },
                        "& .MuiTableCell-body": {
                          border: "none"
                        },
                        "& .MuiTableCell-root": {
                          border: "none !important"
                        },
                        borderBottom:"1px solid #f0f0f0",
                      }}
                    >
                      <TableCell colSpan={columns?.length} style={{ padding: "8px", paddingTop: "0px", border: "none !important", width: "100vw" }}>
                        <Collapse in={expandedRows[rowIndex]} timeout="auto" unmountOnExit 
                          sx={{
                            width : "100%"
                          }}
                        >
                          <Box
                            margin={0}
                            sx={{
                              width: "100%",
                              paddingLeft: "12px",
                              borderLeft: "3px solid rgb(191, 191, 191)",
                            }}
                          >
                            <div
                              className="batch-stock-table-wrapper"
                              style={{
                                borderRadius: "4px",
                                overflow: "hidden",
                                border: "1px solid #eaeaea",
                              }}
                            >
                              <Table sx={{ width: "100%",
                                  minWidth: expandableRowColumns?.reduce(
                                  (sum: any, column: any) => sum + (column.width || 0),
                                  0,
                                ),zIndex: 1, }}>
                                {/* Child Table Head */}
                                <TableHead>
                              <TableRow
                                style={{
                                  backgroundColor: "#fafafa",
                                  zIndex: 2,
                                  display: "flex",
                                  justifyContent: "space-around",
                                }}
                              >
                                {expandableRowColumns?.map((col: any, index: number) => (
                                  <TableCell
                                    key={col.headerName + index}
                                    className="custom_table_header"
                                    sx={{
                                      borderBottom: "0px !important",
                                      textAlign: `${col?.textAlign || "left"}`,
                                      color: "#6f6f6f !important"
                                    }}
                                    style={{
                                      width: col.width,
                                      minWidth: `${col.width}px`,
                                      maxWidth: `${col.width}px`,
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                      whiteSpace: "normal",
                                      alignContent: "center",
                                      backgroundColor: "#fafafa",
                                      position: "relative",
                                    }}
                                  >
                                    {col.renderHeader
                                      ? col.renderHeader(col.headerName)
                                      : col.headerName}
                                    {col.addAsterisk && hasRealChanges && <span style={{position: "absolute", top: 0, right: 0, color: "#B54747"}}>*</span>}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>

                                {/* Child Table Body */}
                                <TableBody style={{border: "none !important"}}>
                                  {(row.mergedRows || []).map((child: any, childIndex: number) => (
                                    <TableRow
                                      key={`child-${rowIndex}-${childIndex}`}
                                      style={{
                                        borderBottom: "1px solid #f0f0f0",
                                        display: "flex",
                                        justifyContent: "space-around",
                                        background: "#fff",
                                        
                                      }}
                                    >
                                      {expandableRowColumns?.map((col, index) => {
                                        let truncatedText = col?.renderCell
                                          ? ""
                                          : truncateText(
                                              child[col.mappedValue]?.toString(),
                                              col?.truncateTextAfterChar ?? +Infinity
                                            );

                                        let inputText = truncatedText;
                                        let formattedValue;
                                        if (col?.currencySymbol) {
                                          truncatedText = `${currencySymbol}\u200A${truncatedText ?? ""}`;
                                          if (isNumeric(inputText)) {
                                            formattedValue = truncateToTwoDecimals(Number(inputText));
                                          } else {
                                            formattedValue = inputText;
                                          }
                                        }
                                        truncatedText = `${currencySymbol}\u200A${formattedValue ?? ""}`;

                                        const isEllipsisColumn =
                                          col.mappedValue === "skuName" ||
                                          col.mappedValue === "outletName" ||
                                          col.mappedValue === "outletCode" ||
                                          col.mappedValue === "remark";


                                        return (
                                          <TableCell
                                            key={index}
                                            sx={{
                                              width: `${col.width}px`,
                                              minWidth: `${col.width}px`,
                                              maxWidth: `${col.width}px`,
                                              fontSize: "12px",
                                              color: "#555",
                                              border: "0px !important",
                                              alignSelf: "center"
                                            }}
                                            className="custom_table_cell"
                                          >
                                            {col.renderCell ? (
                                              col.renderCell(child[col.mappedValue], child)
                                            ) : isEllipsisColumn ? (
                                                <Tooltip title={child[col.mappedValue]?.toString()} arrow>
                                                  <span
                                                    style={{
                                                      display: "inline-block",
                                                      maxWidth: "100%",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {truncatedText}
                                                  </span>
                                                </Tooltip>
                                            ) : (
                                              truncatedText
                                            )}
                                          </TableCell>
                                        );
                                      })}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}

                </React.Fragment>

                );
              }
              return (
              <TableRow
                key={rowIndex}
                style={{
                  borderBottom: rowIndex === paginatedData.length - 1 ? "none" : "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                  // background: row?.source?.toLowerCase() === "ars" ? "rgb(238, 238, 238)" : "transparent",
                }}
              >
                {/* Checkbox column cell */}
                {isCheckboxSelection && (
                  <TableCell
                    sx={{
                      alignContent: "center",
                      width: "50px",
                      minWidth: "50px",
                      maxWidth: "50px",
                      textAlign: "center",
                      border: "0px !important",
                      color: "#7C7C7C",
                      fontSize: "12px",
                    }}
                    className="custom_table_cell"
                  >
                    <Checkbox
                      checked={checkedIdArray.includes(row.id)}
                      disabled={!isRowSelectable(row.id)}
                      onChange={() => handleSelectRow(row.id)}
                      sx={{ padding: "4px" }}
                    />
                  </TableCell>
                )}
                {columns?.map((col, index) => {
                  let truncatedText = col?.renderCell
                    ? ""
                    : truncateText(
                        row[col.mappedValue]?.toString(),
                        col?.truncateTextAfterChar ?? +Infinity
                      );

                  let inputText = truncatedText; // your original string input
                  let formattedValue;
                  if (col?.currencySymbol) {
                    truncatedText = `${currencySymbol}\u200A${truncatedText ?? ""}`;
                    if (isNumeric(inputText)) {
                        formattedValue = truncateToTwoDecimals(Number(inputText));
                    } else {
                        formattedValue = inputText;
                    }
                  }
                  truncatedText = `${currencySymbol}\u200A${formattedValue ?? ""}`;

                  const isEllipsisColumn = col.mappedValue === "skuName" || col.mappedValue === "outletName" || col.mappedValue === "outletCode" || col.mappedValue === "remark";
                  if (col.mappedValue === "skus") {
                    return (
                      <TableCell
                        sx={{
                          alignContent: "center",
                          width: `${col.width}px`,
                          minWidth: `${col.width}px`,
                          maxWidth: `${col.width}px`,
                          textAlign: `${col?.textAlign || "left"}`,
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          border: "0px !important",
                          color: "#7C7C7C",
                          fontSize: "12px",
                        }}
                        key={index}
                        className="custom_table_cell"
                      >
                        {renderSkusCell(row.skus)}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      sx={{
                        alignContent: "center",
                        width: `${col.width}px`,
                        minWidth: `${col.width}px`,
                        maxWidth: `${col.width}px`,
                        textAlign: `${col?.textAlign || "left"}`,
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: isEllipsisColumn ? "nowrap" : "normal", 
                        overflow: isEllipsisColumn ? "hidden" : "visible", 
                        textOverflow: isEllipsisColumn ? "ellipsis" : "clip", 
                        border: "0px !important",
                        color: "#7C7C7C",
                        fontSize: "12px",
                      }}
                      key={index}
                      className="custom_table_cell"
                    >
                      {col.renderCell ? (
                        col.renderCell(row[col.mappedValue], row)
                      ) : isEllipsisColumn ? (
                          <Tooltip title={row[col.mappedValue]?.toString()} arrow>
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {truncatedText}
                            </span>
                          </Tooltip>
                      ) : (
                        truncatedText
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
       
            );
            })
          }
        </>
        
               
              )

      
            )
}


            <TableRow>
              <TableCell
                colSpan={columns.length + (isCheckboxSelection ? 1 : 0)}
                sx={{ padding: "20px 0", borderBottom: "none" }}
              />
            </TableRow>
          </TableBody>
        {totals?.length !== 0 ? (
          <TableFooter
            sx={{
              width: "100%",
              background: "#fafbfc", // Lighter gray background for softer appearance
              zIndex: 2,
              borderTop: "0.5px solid #e9ecef", // Slightly darker border for better definition
              position: "sticky",
            }}
          >
            <TableRow
              style={{
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              {columns.map((col, index) => {

                const value = totals.find(
                  (total: any) => total?.headerName === col?.headerName
                )?.total ?? ""
                
                const adjustedFontSize = (String(value)?.length + String(currencySymbol ?? ``)?.length) > 9 ? "12px !important" : "14px !important"

                return (
                  <TableCell
                    key={index}
                    sx={{
                      alignContent: "center",
                      textAlign: `${
                        totals.find(
                          (total: any) => total?.headerName === col?.headerName
                        )?.textAlign || "left"
                      }`,
                      width: `${col.width}px`,
                      maxWidth: `${col.width}px`,
                      minWidth: `${col.width}px`,
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                      border: "0px !important",
                      color: "#5a6c7d", // Lighter blue-gray text for softer appearance
                      fontWeight: "600",
                    }}
                    className="custom_table_footer_cell"
                  >
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {totals.find(
                        (total: any) => total?.headerName === col?.headerName
                      )?.currencySymbol
                        ? currencySymbol
                        : ""}{`\u200A`}
                      {totals.find(
                        (total: any) => total?.headerName === col?.headerName
                      )?.total ?? ""}
                    </span>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        ) : (
          <></>
        )}
      </Table>
      </TableContainer>
    </div>

     {summaryFooterData && Object.keys(summaryFooterData).length > 0 ? (
        
<div style={{display:"flex",flexDirection:"column",  position: "sticky",
          zIndex: 4, bottom:0}}>
            

<div style={{ 
  width: "100%", 
  backgroundColor: "#F5FBFA", 
  color: "black", 
  height: "40px", 
  padding: "10px", 
  display: "flex", 
  justifyContent: "space-between", 
  alignItems: "center", 
  boxShadow: "0 -2px 5px rgba(0,0,0,0.1)" 
}}>
  <div style={{ display: "flex", alignItems: "center" }}>
    <ShoppingCartIcon
      style={{
        height: "22px",
        width: "14px",
        color:"#00c6b2",
        marginRight: "10px",
      }}
    />
    <span style={{marginRight:"20px"}}>Total Qty <span style={{border:"1px solid #ebebeb", padding:"2px 4px", borderRadius:"5px"}}> {summaryFooterData?.totalQuantityC ?? 0} CS</span> </span> 
    <span>Total load weight  <span style={{border:"1px solid #ebebeb", padding:"2px 4px", borderRadius:"5px"}}> {truncateToTwoDecimals(summaryFooterData?.totalWeightC ?? 0)} Ton</span></span>
  </div>
  <span>Final Amount ₹{truncateToTwoDecimals(summaryFooterData?.totalAmountC ?? 0)}</span>
</div>


    <div className="custom_table_footer_container">
      <TablePagination
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          width: "max-content",
          position: "sticky",
          zIndex: 3,
          bottom: 0,
          "& p": {
            margin: 0,
          },
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {children}
    
    </div>
    </div>

      ) : (
    <div className="custom_table_footer_container">
      <TablePagination
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          width: "max-content",
          position: "sticky",
          zIndex: 3,
          bottom: 0,
          "& p": {
            margin: 0,
          },
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {children}
    </div>
  )} 
  </>
  );
};

export default CustomTablewithTotalInfoV2;


